// src/utils/syncService.ts
// Service to sync pending changes to Supabase when online

import { supabase } from '@/integrations/supabase/client';
import {
  getPendingChanges,
  removePendingChange,
  updateRetryCount,
  saveProfileCache,
} from './offlineStorage';

interface SyncStatus {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: number | null;
  errors: string[];
  changesSynced: number; // Track how many changes were actually synced
}

let syncStatus: SyncStatus = {
  isSyncing: false,
  pendingCount: 0,
  lastSyncTime: null,
  errors: [],
  changesSynced: 0,
};

// Check if online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Sync all pending changes
export async function syncPendingChanges(userId: string): Promise<SyncStatus> {
  if (!isOnline()) {
    return { ...syncStatus, errors: ['No internet connection'] };
  }

  if (syncStatus.isSyncing) {
    return syncStatus;
  }

  syncStatus.isSyncing = true;
  const errors: string[] = [];
  let changesSynced = 0;

  try {
    const pendingChanges = await getPendingChanges(userId);
    syncStatus.pendingCount = pendingChanges.length;

    if (pendingChanges.length === 0) {
      syncStatus.isSyncing = false;
      syncStatus.changesSynced = 0;
      return syncStatus;
    }

    // Sync each change
    for (const change of pendingChanges) {
      try {
        if (change.type === 'profile') {
          const { error } = await supabase
            .from('profiles')
            .update(change.data)
            .eq('id', userId);

          if (error) {
            // If duplicate key error, remove the change (already exists)
            if (error.code === '23505') {
              await removePendingChange(change.id);
              continue;
            }
            throw error;
          }

          // Refresh profile cache after successful update
          const { data: updatedProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (updatedProfile) {
            // Get tokens to cache with profile
            const { data: tokens } = await supabase
              .from('metefy_tokens')
              .select('*')
              .eq('user_id', userId)
              .eq('is_verified', true)
              .order('created_at', { ascending: false });
            
            const formattedTokens = tokens?.map(token => ({
              token: token.token,
              password: token.password,
              dateAdded: new Date(token.created_at).toISOString().split('T')[0],
              name: token.name || null,
              call: token.call || null,
              whatsapp: token.whatsapp || null,
              x_username: token.x_username || null,
              id: token.id
            })) || [];
            
            await saveProfileCache(userId, updatedProfile, formattedTokens);
          }

          await removePendingChange(change.id);
          changesSynced++;
        } else if (change.type === 'avatar') {
          const { error } = await supabase
            .from('profiles')
            .update({ photo_url: change.data.photo_url })
            .eq('id', userId);

          if (error) throw error;
          
          // Refresh profile cache after successful update
          const { data: updatedProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
          
          if (updatedProfile) {
            // Get tokens to cache with profile
            const { data: tokens } = await supabase
              .from('metefy_tokens')
              .select('*')
              .eq('user_id', userId)
              .eq('is_verified', true)
              .order('created_at', { ascending: false });
            
            const formattedTokens = tokens?.map(token => ({
              token: token.token,
              password: token.password,
              dateAdded: new Date(token.created_at).toISOString().split('T')[0],
              name: token.name || null,
              call: token.call || null,
              whatsapp: token.whatsapp || null,
              x_username: token.x_username || null,
              id: token.id
            })) || [];
            
            await saveProfileCache(userId, updatedProfile, formattedTokens);
          }
          
          await removePendingChange(change.id);
          changesSynced++;
        } else if (change.type === 'token') {
          // Handle token updates if needed
          await removePendingChange(change.id);
          changesSynced++;
        }
      } catch (error: any) {
        const retries = change.retries || 0;
        if (retries < 3) {
          await updateRetryCount(change.id, retries + 1);
          errors.push(`Failed to sync ${change.type}: ${error.message}`);
        } else {
          // Max retries reached, remove the change
          await removePendingChange(change.id);
          errors.push(`Max retries reached for ${change.type}, removed from queue`);
        }
      }
    }

    syncStatus.lastSyncTime = Date.now();
    syncStatus.errors = errors;
    syncStatus.changesSynced = changesSynced;
  } catch (error: any) {
    errors.push(`Sync failed: ${error.message}`);
    syncStatus.errors = errors;
  } finally {
    syncStatus.isSyncing = false;
    const remainingChanges = await getPendingChanges(userId);
    syncStatus.pendingCount = remainingChanges.length;
  }

  return syncStatus;
}

// Get sync status
export function getSyncStatus(): SyncStatus {
  return { ...syncStatus };
}

// Listen for online event and auto-sync
export function setupAutoSync(userId: string, onSyncComplete?: (status: SyncStatus) => void) {
  const handleOnline = async () => {
    const status = await syncPendingChanges(userId);
    if (onSyncComplete) {
      onSyncComplete(status);
    }
  };

  window.addEventListener('online', handleOnline);
  
  // Also sync immediately if already online
  if (isOnline()) {
    handleOnline();
  }

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

